/**
 * UI STYLESHEET DELIVERY.
 *
 * `PRESS_CSS` and `MODAL_CSS` existed for weeks and were injected NOWHERE:
 * `src/styles/app.css` carries no `.rga-press` and no `.rga-modal` rule, and
 * nothing in the app imported either constant. So the 95%-and-spring press —
 * "the single cue that makes a UI feel physical" per `docs/UI-STYLE.md` — was
 * dead on screen even in the one place its code was correct.
 *
 * A primitive that needs a caller to remember to paste a stylesheet is a
 * primitive that ships unstyled. `ensureUiStyles()` injects the whole `engine/ui`
 * stylesheet once per document, and every ui module calls it on mount, so a
 * control cannot exist without its own CSS.
 *
 * This is deliberately self-injecting rather than a rule in `app.css`: the
 * fragments live beside the modules that own them, and a screen that never
 * mounts a ui primitive pays nothing. If these rules are later folded into
 * `app.css`, `ensureUiStyles` becomes a no-op and nothing else changes.
 */
import { PRESS_CSS } from './press';
import { MODAL_CSS } from './modal';
import { CONTROLS_CSS } from './controls';
import { LAYOUT_CSS } from './layout-css';
import { BUTTON_CSS, BUTTON_STYLE_MARK } from './button-css';
import { probeSkins, skinCss } from './skins';

const SKIN_MARK = 'ui-skins-css';

/**
 * The bubble's own rules. They live here rather than in `bubble.ts` so that
 * this module can compose the sheet without importing `bubble.ts`, which
 * imports this one. `bubble.ts` re-exports the constant.
 *
 * The shared panel recipe (face gloss, rim, lift, sheen — tokens only, palette
 * pass 2026-10-14), so the bubble matches every modal and toast. There is no
 * entrance transition — the reference captures resolve a panel in ONE frame.
 */
export const BUBBLE_CSS = `
.rga-bubble{position:fixed;z-index:70;max-width:min(20rem,88vw);border-radius:12px;
  border:var(--rga-rim,2px) solid var(--color-border);background:var(--rga-face,none),var(--color-surface);
  color:var(--color-text);box-shadow:var(--rga-lift-1),var(--shadow-lift,0 10px 26px rgba(0,0,0,.42)),var(--rga-sheen);outline:none}
.rga-bubble__body{padding:.6rem .75rem;font-size:.85rem;line-height:1.35}
.rga-bubble__tail{position:absolute;width:0;height:0;margin-left:-9px;
  border-left:9px solid transparent;border-right:9px solid transparent}
.rga-bubble[data-side=below] .rga-bubble__tail{top:-9px;border-bottom:9px solid var(--color-border)}
.rga-bubble[data-side=above] .rga-bubble__tail{bottom:-9px;border-top:9px solid var(--color-border)}
`;

/** Everything `engine/ui` draws. `MODAL_CSS` already contains `PRESS_CSS`. */
export const UI_CSS = `${MODAL_CSS}${BUBBLE_CSS}${CONTROLS_CSS}${LAYOUT_CSS}`;

const MARK = 'ui-css';

/**
 * jsdom's CSS parser predates cascade layers: it rejects the whole sheet and
 * prints it to the console on every mount, which buried the test output of
 * every suite that touches a button. Under jsdom (and only there — every
 * browser this game supports parses layers) the skin goes in without its
 * wrapper. jsdom computes no cascade worth the name anyway.
 */
function layerSupported(doc: Document): boolean {
  const ua = doc.defaultView?.navigator?.userAgent ?? '';
  return !/jsdom/i.test(ua);
}

function unlayered(css: string): string {
  const open = css.indexOf('@layer components{');
  if (open < 0) return css;
  const close = css.lastIndexOf('}');
  return css.slice(0, open) + css.slice(open + '@layer components{'.length, close) + css.slice(close + 1);
}

/** Inject the ui stylesheet once. Safe to call on every mount. */
export function ensureUiStyles(doc: Document = document): void {
  if (doc.querySelector(`style[data-rga="${MARK}"]`) === null) {
    const style = doc.createElement('style');
    style.setAttribute('data-rga', MARK);
    style.textContent = UI_CSS;
    (doc.head ?? doc.documentElement).append(style);
  }
  // The button skin is a SEPARATE tag because it is layered (@layer
  // components) and this sheet must stay unlayered — see button.ts.
  if (doc.querySelector(`style[data-rga="${BUTTON_STYLE_MARK}"]`) === null) {
    const skin = doc.createElement('style');
    skin.setAttribute('data-rga', BUTTON_STYLE_MARK);
    skin.textContent = layerSupported(doc) ? BUTTON_CSS : unlayered(BUTTON_CSS);
    (doc.head ?? doc.documentElement).append(skin);
  }
  // Painted 9-slice skins (skins.ts): rules that apply only once a texture
  // file has loaded, and the once-per-session probe that loads them.
  if (doc.querySelector(`style[data-rga="${SKIN_MARK}"]`) === null) {
    const tex = doc.createElement('style');
    tex.setAttribute('data-rga', SKIN_MARK);
    tex.textContent = skinCss();
    (doc.head ?? doc.documentElement).append(tex);
    if (layerSupported(doc)) probeSkins(doc);
  }
}

export { PRESS_CSS, MODAL_CSS, CONTROLS_CSS, LAYOUT_CSS };
